// 📅 BillFac Agenda — Outlook-achtige kalender (intern, MVP fase 1)
// Views: Dag / Week / Maand • CRUD events • Klik op cel = nieuw event • Klik op event = bewerken
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import API from '@/lib/api';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  startOfDay, endOfDay, addDays, addWeeks, addMonths, subWeeks, subMonths, subDays,
  addYears, subYears,
  format, parseISO, isSameDay, isSameMonth, differenceInMinutes,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  CalendarBlank, CaretLeft, CaretRight, Plus, MapPin, TextAlignLeft, Trash, X, Clock,
} from '@phosphor-icons/react';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#64748B'];
const VIEWS = [
  { key: 'day', label: 'Dag' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Maand' },
];

// ─── Helpers ────────────────────────────────────────────────────
const toISO = (d) => d.toISOString();
const safeParse = (s) => { try { return parseISO(s); } catch { return new Date(s); } };
const fmtDateInput = (d) => format(d, "yyyy-MM-dd'T'HH:mm");
const parseInput = (s) => new Date(s);

// ─── Event-modal (create + edit) ────────────────────────────────
function EventModal({ open, event, defaultStart, onClose, onSave, onDelete }) {
  const isEdit = Boolean(event?.id);
  const [form, setForm] = useState({
    titel: '', start: '', eind: '', all_day: false,
    locatie: '', beschrijving: '', kleur: COLORS[0],
    recurrence_enabled: false, freq: 'WEEKLY', interval: 1, end_mode: 'never', until: '', count: 4,
  });
  const [saving, setSaving] = useState(false);
  const isRecurringInstance = Boolean(event?.recurrence_parent_id || (event?.id || '').includes('__'));

  useEffect(() => {
    if (!open) return;
    if (event) {
      const rec = event.recurrence || null;
      setForm({
        titel: event.titel || '',
        start: fmtDateInput(safeParse(event.start)),
        eind: fmtDateInput(safeParse(event.eind)),
        all_day: event.all_day || false,
        locatie: event.locatie || '',
        beschrijving: event.beschrijving || '',
        kleur: event.kleur || COLORS[0],
        recurrence_enabled: Boolean(rec?.freq),
        freq: rec?.freq || 'WEEKLY',
        interval: rec?.interval || 1,
        end_mode: rec?.until ? 'until' : (rec?.count ? 'count' : 'never'),
        until: rec?.until ? (rec.until || '').slice(0, 10) : '',
        count: rec?.count || 4,
      });
    } else if (defaultStart) {
      const s = defaultStart;
      const e = new Date(s.getTime() + 60 * 60 * 1000); // +1 uur
      setForm({
        titel: '', start: fmtDateInput(s), eind: fmtDateInput(e),
        all_day: false, locatie: '', beschrijving: '', kleur: COLORS[0],
        recurrence_enabled: false, freq: 'WEEKLY', interval: 1, end_mode: 'never', until: '', count: 4,
      });
    }
  }, [open, event, defaultStart]);

  if (!open) return null;

  const save = async () => {
    if (!form.titel.trim()) { alert('Geef een titel'); return; }
    setSaving(true);
    try {
      const payload = {
        titel: form.titel.trim(),
        start: toISO(parseInput(form.start)),
        eind: toISO(parseInput(form.eind)),
        all_day: form.all_day,
        locatie: form.locatie.trim(),
        beschrijving: form.beschrijving.trim(),
        kleur: form.kleur,
      };
      if (form.recurrence_enabled) {
        payload.recurrence = {
          freq: form.freq, interval: Number(form.interval) || 1,
          until: form.end_mode === 'until' && form.until ? form.until : '',
          count: form.end_mode === 'count' ? Number(form.count) || 0 : 0,
        };
      } else if (isEdit && event?.recurrence) {
        payload.recurrence = null; // verwijder recurrence
      }
      if (isEdit) {
        // Voor recurring instance: vraag scope
        let scope = 'series';
        if (isRecurringInstance) {
          const choice = window.confirm('Wijziging toepassen op:\n\n✓ OK = Hele serie\n✗ Annuleren = Alleen deze afspraak');
          scope = choice ? 'series' : 'this';
        }
        await API.put(`/agenda/events/${event.id}?scope=${scope}`, payload);
      } else {
        if (event?.bron_mail_id) {
          payload.bron_mail_id = event.bron_mail_id;
          payload.bron_mail_subject = event.bron_mail_subject;
        }
        await API.post('/agenda/events', payload);
      }
      onSave();
      onClose();
    } catch (e) {
      alert('Opslaan mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const del = async () => {
    let scope = 'series';
    if (isRecurringInstance) {
      const choice = window.confirm('Verwijderen:\n\n✓ OK = Hele serie\n✗ Annuleren = Alleen deze afspraak');
      scope = choice ? 'series' : 'this';
    } else if (!window.confirm('Event verwijderen?')) {
      return;
    }
    try {
      await API.delete(`/agenda/events/${event.id}?scope=${scope}`);
      onDelete();
      onClose();
    } catch (e) {
      alert('Verwijderen mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="agenda-event-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ background: form.kleur + '15' }}>
          <h3 className="text-base font-bold text-gray-900">
            {isEdit ? '📝 Event bewerken' : '➕ Nieuw event'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-testid="agenda-modal-close"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <input
            type="text" placeholder="Titel van event"
            value={form.titel} onChange={e => setForm({ ...form, titel: e.target.value })}
            className="w-full text-base font-semibold border-b border-gray-300 focus:border-[#002FA7] outline-none py-1"
            data-testid="agenda-event-titel"
          />

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-500">Start</label>
              <input
                type="datetime-local" value={form.start}
                onChange={e => setForm({ ...form, start: e.target.value })}
                className="w-full text-sm border rounded px-2 py-1.5"
                data-testid="agenda-event-start"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-500">Eind</label>
              <input
                type="datetime-local" value={form.eind}
                onChange={e => setForm({ ...form, eind: e.target.value })}
                className="w-full text-sm border rounded px-2 py-1.5"
                data-testid="agenda-event-eind"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.all_day} onChange={e => setForm({ ...form, all_day: e.target.checked })} />
            Hele dag
          </label>

          <div>
            <label className="text-[10px] uppercase font-bold text-gray-500 flex items-center gap-1"><MapPin size={12} /> Locatie</label>
            <input
              type="text" placeholder="Adres / Teams-link / lokaal"
              value={form.locatie} onChange={e => setForm({ ...form, locatie: e.target.value })}
              className="w-full text-sm border rounded px-2 py-1.5"
              data-testid="agenda-event-locatie"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase font-bold text-gray-500 flex items-center gap-1"><TextAlignLeft size={12} /> Beschrijving</label>
            <textarea
              placeholder="Notities, agenda, deelnemers..."
              value={form.beschrijving} onChange={e => setForm({ ...form, beschrijving: e.target.value })}
              rows={3} className="w-full text-sm border rounded px-2 py-1.5"
              data-testid="agenda-event-beschrijving"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase font-bold text-gray-500">Kleur</label>
            <div className="flex gap-2 mt-1">
              {COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setForm({ ...form, kleur: c })}
                  className={`w-6 h-6 rounded-full border-2 ${form.kleur === c ? 'border-gray-900 scale-110' : 'border-gray-300'} transition`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>

          {/* 🔁 Herhalen — recurrence */}
          <div className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded p-2 space-y-2">
            <label className="flex items-center gap-2 text-xs font-bold text-violet-700">
              <input
                type="checkbox" checked={form.recurrence_enabled}
                onChange={e => setForm({ ...form, recurrence_enabled: e.target.checked })}
                data-testid="agenda-event-recurrence-enabled"
              />
              🔁 Herhalen
            </label>
            {form.recurrence_enabled && (
              <div className="space-y-2 pl-5">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-500">Frequentie</label>
                    <select
                      value={form.freq} onChange={e => setForm({ ...form, freq: e.target.value })}
                      className="w-full text-sm border rounded px-2 py-1.5"
                      data-testid="agenda-recurrence-freq"
                    >
                      <option value="DAILY">Dagelijks</option>
                      <option value="WEEKLY">Wekelijks</option>
                      <option value="MONTHLY">Maandelijks</option>
                      <option value="YEARLY">Jaarlijks</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-500">Elke</label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number" min="1" max="99" value={form.interval}
                        onChange={e => setForm({ ...form, interval: e.target.value })}
                        className="w-16 text-sm border rounded px-2 py-1.5"
                        data-testid="agenda-recurrence-interval"
                      />
                      <span className="text-xs text-gray-600">
                        {form.freq === 'DAILY' ? 'dag(en)' : form.freq === 'WEEKLY' ? 'week/weken' : form.freq === 'MONTHLY' ? 'maand(en)' : 'jaar/jaren'}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-gray-500">Eindigt</label>
                  <div className="grid grid-cols-3 gap-1 mt-1">
                    {[
                      { v: 'never', label: 'Nooit' },
                      { v: 'count', label: 'Na N keer' },
                      { v: 'until', label: 'Op datum' },
                    ].map(o => (
                      <button
                        key={o.v} type="button"
                        onClick={() => setForm({ ...form, end_mode: o.v })}
                        className={`text-xs py-1 rounded font-semibold ${form.end_mode === o.v ? 'bg-violet-600 text-white' : 'bg-white border'}`}
                      >{o.label}</button>
                    ))}
                  </div>
                  {form.end_mode === 'count' && (
                    <input
                      type="number" min="1" max="999" value={form.count}
                      onChange={e => setForm({ ...form, count: e.target.value })}
                      className="w-full text-sm border rounded px-2 py-1.5 mt-1"
                      placeholder="Aantal keer"
                    />
                  )}
                  {form.end_mode === 'until' && (
                    <input
                      type="date" value={form.until}
                      onChange={e => setForm({ ...form, until: e.target.value })}
                      className="w-full text-sm border rounded px-2 py-1.5 mt-1"
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {event?.bron_mail_subject && (
            <div className="text-[11px] bg-purple-50 border border-purple-200 rounded px-2 py-1.5 text-purple-700">
              📧 Vanuit mail: <span className="font-semibold">{event.bron_mail_subject}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <div>
            {isEdit && (
              <button
                onClick={del}
                className="text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1"
                data-testid="agenda-event-delete"
              >
                <Trash size={12} /> Verwijderen
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded">Annuleer</button>
            <button
              onClick={save} disabled={saving}
              className="px-4 py-1.5 text-sm font-semibold text-white rounded"
              style={{ background: form.kleur }}
              data-testid="agenda-event-save"
            >
              {saving ? 'Opslaan...' : (isEdit ? 'Bewerken' : 'Aanmaken')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Event-blok in cel — met drag&drop + optionele resize ────────
const EventBlock = ({ event, onClick, onDragStart, onResize, slotPxPerMin, compact }) => {
  const start = safeParse(event.start);
  const eind = safeParse(event.eind);
  const dur = differenceInMinutes(eind, start);
  const isRecurring = Boolean(event.recurrence || event.recurrence_parent_id);
  const [resizing, setResizing] = useState(false);
  const [previewEind, setPreviewEind] = useState(null);

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const origEind = safeParse(event.eind);
    const origStart = safeParse(event.start);
    const minDuration = 15; // min 15 minuten
    const pxPerMin = slotPxPerMin || 0.8;
    let lastEind = origEind;

    setResizing(true);
    const onMove = (ev) => {
      const dy = ev.clientY - startY;
      let deltaMin = Math.round(dy / pxPerMin / 15) * 15;
      const newEindCandidate = new Date(origEind.getTime() + deltaMin * 60000);
      const currentDur = (newEindCandidate.getTime() - origStart.getTime()) / 60000;
      if (currentDur < minDuration) {
        lastEind = new Date(origStart.getTime() + minDuration * 60000);
      } else {
        lastEind = newEindCandidate;
      }
      setPreviewEind(lastEind);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setResizing(false);
      setPreviewEind(null);
      if (lastEind.getTime() !== origEind.getTime() && onResize) {
        onResize(event, lastEind);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const effectiveEind = previewEind || eind;
  const effectiveDur = differenceInMinutes(effectiveEind, start);

  return (
    <div
      className="relative rounded overflow-hidden"
      style={{
        background: event.kleur || COLORS[0],
        outline: resizing ? '2px solid #1F2937' : 'none',
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClick(event); }}
        draggable={Boolean(onDragStart) && !resizing}
        onDragStart={(e) => { if (onDragStart) onDragStart(e, event); }}
        className="block w-full text-left px-1.5 py-0.5 text-[11px] text-white truncate hover:opacity-90 transition cursor-grab active:cursor-grabbing"
        title={`${event.titel}${isRecurring ? ' 🔁' : ''} • ${format(start, 'HH:mm')} (${effectiveDur}min)${event.locatie ? ' • ' + event.locatie : ''}`}
        data-testid={`agenda-event-block-${event.id}`}
      >
        {!event.all_day && !compact && <span className="opacity-80 mr-1">{format(start, 'HH:mm')}</span>}
        <span className="font-medium">{event.all_day ? '⏰' : ''} {isRecurring && '🔁'} {event.titel}</span>
        {resizing && previewEind && (
          <div className="text-[10px] mt-0.5 bg-black/40 rounded px-1">
            → {format(previewEind, 'HH:mm')} ({effectiveDur}min)
          </div>
        )}
      </button>
      {onResize && (
        <div
          onMouseDown={handleResizeMouseDown}
          className="absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize hover:bg-white/30 transition"
          title="Sleep om duur aan te passen"
          data-testid={`agenda-event-resize-${event.id}`}
        />
      )}
    </div>
  );
};

// ─── MAAND VIEW ──────────────────────────────────────────────────
function MonthView({ cursor, events, feestdagen, onEventClick, onCellClick, onEventDragStart, onCellDrop }) {
  const grid = useMemo(() => {
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = [];
    let d = gridStart;
    while (d <= gridEnd) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [cursor]);

  // Helper: event spant deze dag (start ≤ d ≤ eind)
  const spansDay = useCallback((e, d) => {
    const s = safeParse(e.start);
    const en = safeParse(e.eind);
    // Vergelijk op datum-niveau (zonder tijd)
    const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const dayEnd = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
    return s <= dayEnd && en >= dayStart;
  }, []);

  const dayEvents = useCallback((d) => events.filter(e => spansDay(e, d)), [events, spansDay]);
  const dayHeaders = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
  const today = new Date();
  const [dragOver, setDragOver] = useState(null);

  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid="agenda-month-view">
      <div className="grid grid-cols-7 bg-gray-100 border-b">
        {dayHeaders.map(h => <div key={h} className="text-center py-2 text-xs font-bold text-gray-600">{h}</div>)}
      </div>
      <div className="grid grid-cols-7 grid-rows-6" style={{ minHeight: 600 }}>
        {grid.map((d, i) => {
          const isInMonth = isSameMonth(d, cursor);
          const isToday = isSameDay(d, today);
          const evts = dayEvents(d);
          const dStr = format(d, 'yyyy-MM-dd');
          const isDragOver = dragOver === dStr;
          const feestdagNaam = (feestdagen || {})[dStr];
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          // Achtergrond: feestdag = rood-getint, weekend = lichtgrijs
          const cellBg = isDragOver
            ? 'bg-violet-100 ring-2 ring-violet-500'
            : feestdagNaam
              ? (isInMonth ? 'bg-rose-50 hover:bg-rose-100' : 'bg-rose-25')
              : isWeekend
                ? (isInMonth ? 'bg-slate-50 hover:bg-blue-50' : 'bg-gray-100')
                : (isInMonth ? 'bg-white hover:bg-blue-50' : 'bg-gray-50');
          return (
            <div
              key={i}
              onClick={() => onCellClick(d)}
              onDragOver={(e) => { e.preventDefault(); setDragOver(dStr); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => {
                e.preventDefault(); setDragOver(null);
                if (onCellDrop) onCellDrop(e, d, 'month');
              }}
              className={`border-r border-b p-1 cursor-pointer transition ${cellBg}`}
              data-testid={`agenda-cell-${dStr}`}
              title={feestdagNaam || ''}
            >
              <div className="flex items-center justify-between mb-1">
                <div className={`text-[11px] font-semibold ${isToday ? 'inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#002FA7] text-white' : (feestdagNaam ? 'text-rose-700' : (isInMonth ? 'text-gray-700' : 'text-gray-400'))}`}>
                  {format(d, 'd')}
                </div>
                {feestdagNaam && (
                  <span className="text-[9px] text-rose-700 font-bold bg-rose-100 px-1 rounded truncate max-w-[100px]" title={feestdagNaam} data-testid={`feestdag-${dStr}`}>
                    🎉 {feestdagNaam}
                  </span>
                )}
              </div>
              <div className="space-y-0.5 overflow-hidden" style={{ maxHeight: 80 }}>
                {evts.slice(0, 3).map(e => <EventBlock key={e.id} event={e} onClick={onEventClick} onDragStart={onEventDragStart} compact />)}
                {evts.length > 3 && <div className="text-[10px] text-gray-500 px-1">+{evts.length - 3} meer</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── WEEK VIEW (uurraster) ───────────────────────────────────────
function WeekView({ cursor, events, feestdagen, onEventClick, onCellClick, onEventDragStart, onCellDrop, onEventResize }) {
  const days = useMemo(() => {
    const start = startOfWeek(cursor, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [cursor]);
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);
  const today = new Date();

  const eventsForSlot = (d, h) => events.filter(e => {
    if (e.all_day) return false; // all-day in aparte rij
    const s = safeParse(e.start);
    return isSameDay(s, d) && s.getHours() === h;
  });

  // All-day events per dag (incl. multi-day spanning)
  const allDayForDay = (d) => events.filter(e => {
    if (!e.all_day) return false;
    const s = safeParse(e.start);
    const en = safeParse(e.eind);
    const dStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const dEnd = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
    return s <= dEnd && en >= dStart;
  });

  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid="agenda-week-view">
      <div className="grid border-b" style={{ gridTemplateColumns: '50px repeat(7, 1fr)' }}>
        <div className="bg-gray-100 py-2 text-center text-[10px] text-gray-500"></div>
        {days.map((d, i) => {
          const isToday = isSameDay(d, today);
          const dStr = format(d, 'yyyy-MM-dd');
          const feestdagNaam = (feestdagen || {})[dStr];
          return (
            <div key={i} className={`text-center py-2 border-l ${isToday ? 'bg-blue-50' : (feestdagNaam ? 'bg-rose-50' : 'bg-gray-100')}`} title={feestdagNaam || ''}>
              <div className="text-[10px] text-gray-600 uppercase">{format(d, 'EEE', { locale: nl })}</div>
              <div className={`text-sm font-bold ${isToday ? 'text-[#002FA7]' : (feestdagNaam ? 'text-rose-700' : 'text-gray-800')}`}>{format(d, 'd MMM', { locale: nl })}</div>
              {feestdagNaam && <div className="text-[9px] text-rose-700 font-bold mt-0.5 truncate px-1" data-testid={`feestdag-${dStr}`}>🎉 {feestdagNaam}</div>}
            </div>
          );
        })}
      </div>

      {/* All-day rij — verschijnt alleen als ergens all-day events zijn */}
      {days.some(d => allDayForDay(d).length > 0) && (
        <div className="grid border-b bg-violet-50/30" style={{ gridTemplateColumns: '50px repeat(7, 1fr)' }}>
          <div className="text-[9px] text-violet-600 text-right pr-2 py-1 border-r font-bold uppercase">Hele dag</div>
          {days.map((d, i) => {
            const allDay = allDayForDay(d);
            return (
              <div key={i} className="border-l p-0.5 space-y-0.5" style={{ minHeight: 24 }}>
                {allDay.map(e => (
                  <button
                    key={e.id}
                    onClick={(ev) => { ev.stopPropagation(); onEventClick(e); }}
                    className="block w-full text-left rounded px-1.5 py-0.5 text-[11px] text-white truncate hover:opacity-90"
                    style={{ background: e.kleur }}
                    title={e.titel}
                  >
                    ⏰ {e.titel}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
      <div className="overflow-y-auto" style={{ maxHeight: 600 }}>
        {hours.map(h => (
          <div key={h} className="grid border-b" style={{ gridTemplateColumns: '50px repeat(7, 1fr)' }}>
            <div className="text-[10px] text-gray-500 text-right pr-2 py-1 border-r">{String(h).padStart(2, '0')}:00</div>
            {days.map((d, i) => {
              const evts = eventsForSlot(d, h);
              const cellDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, 0, 0);
              return (
                <div
                  key={i}
                  onClick={() => onCellClick(cellDate)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); if (onCellDrop) onCellDrop(e, cellDate, 'week'); }}
                  className="border-l p-0.5 cursor-pointer hover:bg-blue-50 transition relative"
                  style={{ minHeight: 36 }}
                  data-testid={`agenda-week-slot-${format(d, 'yyyy-MM-dd')}-${h}`}
                >
                  <div className="space-y-0.5">
                    {evts.map(e => <EventBlock key={e.id} event={e} onClick={onEventClick} onDragStart={onEventDragStart} onResize={onEventResize} slotPxPerMin={36 / 60} />)}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── DayEventCard — rich event-card voor dag-view met resize-handle ─
function DayEventCard({ event, onEventClick, onEventDragStart, onEventResize }) {
  const [resizing, setResizing] = useState(false);
  const [previewEind, setPreviewEind] = useState(null);
  const isRecurring = Boolean(event.recurrence || event.recurrence_parent_id);

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const origEind = safeParse(event.eind);
    const origStart = safeParse(event.start);
    const pxPerMin = 48 / 60; // dag-view slot = 48px per uur
    let lastEind = origEind;
    setResizing(true);
    const onMove = (ev) => {
      const dy = ev.clientY - startY;
      const deltaMin = Math.round(dy / pxPerMin / 15) * 15;
      let candidate = new Date(origEind.getTime() + deltaMin * 60000);
      if ((candidate.getTime() - origStart.getTime()) / 60000 < 15) {
        candidate = new Date(origStart.getTime() + 15 * 60000);
      }
      lastEind = candidate;
      setPreviewEind(lastEind);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setResizing(false);
      setPreviewEind(null);
      if (lastEind.getTime() !== origEind.getTime() && onEventResize) {
        onEventResize(event, lastEind);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const displayEind = previewEind || safeParse(event.eind);

  return (
    <div className="relative rounded overflow-hidden" style={{ background: event.kleur, outline: resizing ? '2px solid #1F2937' : 'none' }}>
      <button
        onClick={(ev) => { ev.stopPropagation(); onEventClick(event); }}
        draggable={Boolean(onEventDragStart) && !resizing}
        onDragStart={(ev) => onEventDragStart && onEventDragStart(ev, event)}
        className="block w-full text-left px-2 py-1 text-sm text-white hover:opacity-90 cursor-grab active:cursor-grabbing"
        data-testid={`agenda-day-event-${event.id}`}
      >
        <div className="font-semibold">{isRecurring ? '🔁 ' : ''}{event.titel}</div>
        <div className="text-[11px] opacity-90 flex items-center gap-2 mt-0.5">
          <Clock size={11} /> {format(safeParse(event.start), 'HH:mm')} → {format(displayEind, 'HH:mm')}
          {event.locatie && <><MapPin size={11} /> {event.locatie}</>}
          {resizing && <span className="ml-auto bg-black/40 px-1 rounded text-[10px]">⇕ {differenceInMinutes(displayEind, safeParse(event.start))}min</span>}
        </div>
      </button>
      {onEventResize && (
        <div
          onMouseDown={handleResizeMouseDown}
          className="absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize hover:bg-white/30 transition"
          title="Sleep om duur aan te passen"
          data-testid={`agenda-day-event-resize-${event.id}`}
        />
      )}
    </div>
  );
}

// ─── DAG VIEW ────────────────────────────────────────────────────
function DayView({ cursor, events, feestdagen, onEventClick, onCellClick, onEventDragStart, onCellDrop, onEventResize }) {
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);
  // Event spant deze dag?
  const spansThisDay = (e) => {
    const s = safeParse(e.start);
    const en = safeParse(e.eind);
    const dStart = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
    const dEnd = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), 23, 59, 59);
    return s <= dEnd && en >= dStart;
  };
  const allDayEvents = events.filter(e => e.all_day && spansThisDay(e));
  const hourlyEvents = events.filter(e => !e.all_day && isSameDay(safeParse(e.start), cursor));
  const totalCount = allDayEvents.length + hourlyEvents.length;
  const dStr = format(cursor, 'yyyy-MM-dd');
  const feestdagNaam = (feestdagen || {})[dStr];

  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid="agenda-day-view">
      <div className={`border-b py-3 text-center ${feestdagNaam ? 'bg-rose-50' : 'bg-gray-100'}`}>
        <div className={`text-lg font-bold ${feestdagNaam ? 'text-rose-700' : 'text-[#002FA7]'}`}>{format(cursor, 'EEEE d MMMM yyyy', { locale: nl })}</div>
        {feestdagNaam && <div className="text-sm text-rose-700 font-semibold mt-1" data-testid={`feestdag-${dStr}`}>🎉 {feestdagNaam}</div>}
        <div className="text-xs text-gray-500">{totalCount} event(s)</div>
      </div>

      {/* All-day banner bovenaan */}
      {allDayEvents.length > 0 && (
        <div className="border-b bg-violet-50/40 px-3 py-2 space-y-1">
          <div className="text-[10px] uppercase font-bold text-violet-600 mb-1">Hele dag</div>
          {allDayEvents.map(e => (
            <button
              key={e.id}
              onClick={() => onEventClick(e)}
              className="block w-full text-left rounded px-2 py-1 text-sm text-white hover:opacity-90"
              style={{ background: e.kleur }}
              data-testid={`agenda-day-allday-${e.id}`}
            >
              <span className="font-semibold">⏰ {e.titel}</span>
              {e.locatie && <span className="text-[11px] opacity-90 ml-2">• {e.locatie}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="overflow-y-auto" style={{ maxHeight: 600 }}>
        {hours.map(h => {
          const evts = hourlyEvents.filter(e => safeParse(e.start).getHours() === h);
          const cellDate = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), h, 0, 0);
          return (
            <div
              key={h}
              onClick={() => onCellClick(cellDate)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (onCellDrop) onCellDrop(e, cellDate, 'day'); }}
              className="grid border-b cursor-pointer hover:bg-blue-50 transition"
              style={{ gridTemplateColumns: '60px 1fr', minHeight: 48 }}
              data-testid={`agenda-day-slot-${h}`}
            >
              <div className="text-xs text-gray-500 text-right pr-2 py-2 border-r">{String(h).padStart(2, '0')}:00</div>
              <div className="p-1 space-y-1">
                {evts.map(e => (
                  <DayEventCard key={e.id} event={e} onEventClick={onEventClick} onEventDragStart={onEventDragStart} onEventResize={onEventResize} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── MAIN PAGE ───────────────────────────────────────────────────
export default function AgendaPage() {
  const [view, setView] = useState('month');
  const initialCursor = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const d = params.get('d');
      if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return new Date(d + 'T12:00:00');
    } catch { /* ignore */ }
    return new Date();
  }, []);
  const [cursor, setCursor] = useState(initialCursor);
  const [events, setEvents] = useState([]);
  const [leveringEvents, setLeveringEvents] = useState([]); // 🚚 geplande leveringen (read-only)
  const [toonLeveringen, setToonLeveringen] = useState(() => {
    try { return localStorage.getItem('agenda_toon_leveringen') !== '0'; } catch { return true; }
  });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, event: null, defaultStart: null });
  // 🇧🇪 Feestdagen — geladen per jaar via tenant-registratieland
  const [feestdagen, setFeestdagen] = useState({}); // { 'YYYY-MM-DD': 'Naam' }
  const [feestdagenInfo, setFeestdagenInfo] = useState(null); // { land, jaar, totaal }
  const [loadedFeestjaren, setLoadedFeestjaren] = useState(new Set());

  // Laad feestdagen voor jaar van cursor (en aangrenzende jaren bij overgang)
  useEffect(() => {
    const jaar = cursor.getFullYear();
    if (loadedFeestjaren.has(jaar)) return;
    let cancelled = false;
    API.get(`/agenda/feestdagen?jaar=${jaar}`)
      .then(r => {
        if (cancelled) return;
        const map = {};
        for (const item of (r.data?.items || [])) {
          map[item.datum] = item.naam;
        }
        setFeestdagen(prev => ({ ...prev, ...map }));
        setFeestdagenInfo({ land: r.data?.land, jaar, totaal: r.data?.totaal });
        setLoadedFeestjaren(prev => new Set([...prev, jaar]));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [cursor, loadedFeestjaren]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Bredere range om naviga­tie soepel te maken
      const start = startOfMonth(subMonths(cursor, 1)).toISOString();
      const end = endOfMonth(addMonths(cursor, 1)).toISOString();
      const r = await API.get(`/agenda/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);
      setEvents(r.data?.events || []);
      // 🚚 Geplande leveringen — parallel ophalen (toegevoegd aan events bij render via merge)
      if (toonLeveringen) {
        try {
          const lr = await API.get(`/leverbonnen/planning?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);
          setLeveringEvents(lr.data?.events || []);
        } catch (err) {
          console.warn('Leveringen-planning niet geladen', err);
          setLeveringEvents([]);
        }
      } else {
        setLeveringEvents([]);
      }
    } catch (e) {
      console.error('Load events failed', e);
    }
    setLoading(false);
  }, [cursor, toonLeveringen]);

  useEffect(() => { load(); }, [load]);

  const navPrev = () => {
    if (view === 'day') setCursor(d => subDays(d, 1));
    else if (view === 'week') setCursor(d => subWeeks(d, 1));
    else setCursor(d => subMonths(d, 1));
  };
  const navNext = () => {
    if (view === 'day') setCursor(d => addDays(d, 1));
    else if (view === 'week') setCursor(d => addWeeks(d, 1));
    else setCursor(d => addMonths(d, 1));
  };
  const goToday = () => setCursor(new Date());

  const onEventClick = (e) => {
    // 🚚 Leverbon-planning events zijn read-only → navigeer naar leveringsbonnen-lijst
    if (e?.type === 'leverbon_planning' && e?.leverbon_id) {
      window.location.href = `/leveringsbonnen?focus=${e.leverbon_id}`;
      return;
    }
    setModal({ open: true, event: e, defaultStart: null });
  };
  const onCellClick = (d) => setModal({ open: true, event: null, defaultStart: d });
  const onNew = () => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    now.setHours(now.getHours() + 1);
    setModal({ open: true, event: null, defaultStart: now });
  };

  // 🖱️ Drag & drop — verplaats events naar nieuwe dag/uur
  const onEventDragStart = (e, event) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify(event));
  };
  const onCellDrop = async (e, targetDate, viewMode) => {
    let dragged;
    try { dragged = JSON.parse(e.dataTransfer.getData('application/json')); } catch { return; }
    if (!dragged?.id) return;
    const oldStart = safeParse(dragged.start);
    const oldEnd = safeParse(dragged.eind);
    const duration = oldEnd.getTime() - oldStart.getTime();

    let newStart;
    if (viewMode === 'month' || dragged.all_day) {
      // Behoud uur en min van origineel start
      newStart = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(),
                          oldStart.getHours(), oldStart.getMinutes(), 0, 0);
    } else {
      // Week/Dag: gebruik exacte targetDate (uur + dag)
      newStart = new Date(targetDate);
    }
    const newEnd = new Date(newStart.getTime() + duration);

    // Recurring instance → vraag scope
    const isInstance = (dragged.id || '').includes('__') || dragged.recurrence_parent_id;
    let scope = 'series';
    if (isInstance) {
      const choice = window.confirm('Verplaats:\n\n✓ OK = Hele serie\n✗ Annuleren = Alleen deze afspraak');
      scope = choice ? 'series' : 'this';
    }

    try {
      await API.put(`/agenda/events/${dragged.id}?scope=${scope}`, {
        start: newStart.toISOString(),
        eind: newEnd.toISOString(),
      });
      load();
    } catch (err) {
      alert('Verplaatsen mislukt: ' + (err.response?.data?.detail || err.message));
    }
  };

  // 📏 Resize handler — wijzig alleen eind-tijd
  const onEventResize = async (event, newEind) => {
    const isInstance = (event.id || '').includes('__') || event.recurrence_parent_id;
    let scope = 'series';
    if (isInstance) {
      const choice = window.confirm('Duur aanpassen:\n\n✓ OK = Hele serie\n✗ Annuleren = Alleen deze afspraak');
      scope = choice ? 'series' : 'this';
    }
    try {
      await API.put(`/agenda/events/${event.id}?scope=${scope}`, {
        start: event.start,
        eind: newEind.toISOString(),
      });
      load();
    } catch (err) {
      alert('Duur aanpassen mislukt: ' + (err.response?.data?.detail || err.message));
    }
  };

  const headerLabel = useMemo(() => {
    if (view === 'day') return format(cursor, 'EEEE d MMMM yyyy', { locale: nl });
    if (view === 'week') {
      const s = startOfWeek(cursor, { weekStartsOn: 1 });
      const e = endOfWeek(cursor, { weekStartsOn: 1 });
      return `${format(s, 'd MMM', { locale: nl })} – ${format(e, 'd MMM yyyy', { locale: nl })}`;
    }
    return format(cursor, 'MMMM yyyy', { locale: nl });
  }, [cursor, view]);

  // 🚚 Combineer reguliere events met leverbon-planning events voor de viewers
  const allEvents = useMemo(() => {
    if (!toonLeveringen || leveringEvents.length === 0) return events;
    return [...events, ...leveringEvents];
  }, [events, leveringEvents, toonLeveringen]);

  // 📊 Tel leveringen in huidige zicht-range
  const leveringenCount = leveringEvents.length;

  const toggleLeveringen = () => {
    setToonLeveringen(v => {
      const nw = !v;
      try { localStorage.setItem('agenda_toon_leveringen', nw ? '1' : '0'); } catch {}
      return nw;
    });
  };

  return (
    <div className="p-4 space-y-3" data-testid="agenda-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CalendarBlank size={24} weight="duotone" className="text-[#002FA7]" />
          Agenda
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={toggleLeveringen}
            className={`px-3 py-1.5 rounded font-semibold text-xs inline-flex items-center gap-1.5 border transition ${
              toonLeveringen
                ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
            title={toonLeveringen ? 'Verberg geplande leveringen' : 'Toon geplande leveringen'}
            data-testid="agenda-toggle-leveringen"
          >
            <span className={toonLeveringen ? '' : 'opacity-40'}>🚚</span>
            Leveringen
            {toonLeveringen && leveringenCount > 0 && (
              <span className="ml-1 bg-emerald-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold" data-testid="agenda-leveringen-count">
                {leveringenCount}
              </span>
            )}
          </button>
          <button
            onClick={onNew}
            className="bg-[#002FA7] text-white px-3 py-1.5 rounded font-semibold text-sm hover:bg-[#001D6F] flex items-center gap-1"
            data-testid="agenda-new-event"
          >
            <Plus size={14} weight="bold" /> Nieuw event
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2 bg-white border rounded-lg p-2">
        <div className="flex items-center gap-1">
          {/* Jaar terug */}
          <button
            onClick={() => setCursor(d => subYears(d, 1))}
            className="px-2 py-1.5 hover:bg-gray-100 rounded text-xs font-bold text-gray-600"
            data-testid="agenda-prev-year"
            title="Vorig jaar"
          >
            «
          </button>
          {/* Maand/week/dag terug */}
          <button onClick={navPrev} className="p-1.5 hover:bg-gray-100 rounded" data-testid="agenda-prev" title="Vorige"><CaretLeft size={16} /></button>
          <button onClick={goToday} className="px-3 py-1 text-xs font-semibold text-gray-700 border rounded hover:bg-gray-100" data-testid="agenda-today">Vandaag</button>
          {/* Maand/week/dag vooruit */}
          <button onClick={navNext} className="p-1.5 hover:bg-gray-100 rounded" data-testid="agenda-next" title="Volgende"><CaretRight size={16} /></button>
          {/* Jaar vooruit */}
          <button
            onClick={() => setCursor(d => addYears(d, 1))}
            className="px-2 py-1.5 hover:bg-gray-100 rounded text-xs font-bold text-gray-600"
            data-testid="agenda-next-year"
            title="Volgend jaar"
          >
            »
          </button>
          {/* Klikbaar label voor direct-jaar/maand picker */}
          <DatePickerLabel cursor={cursor} setCursor={setCursor} label={headerLabel} />
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded p-0.5">
          {VIEWS.map(v => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={`px-3 py-1 text-xs font-semibold rounded transition ${view === v.key ? 'bg-white shadow text-[#002FA7]' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid={`agenda-view-${v.key}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-xs text-gray-500">Laden...</div>}

      {/* 🎉 Feestdagen-info badge (van tenant-registratieland) */}
      {feestdagenInfo && (
        <div className="text-[11px] text-gray-500 flex items-center gap-2" data-testid="feestdagen-info">
          <span className="bg-rose-100 text-rose-700 px-2 py-0.5 rounded font-semibold">🎉 Feestdagen actief</span>
          <span>Land: <strong className="text-gray-700">{feestdagenInfo.land}</strong> ({feestdagenInfo.totaal} wettelijke feestdagen in {feestdagenInfo.jaar})</span>
        </div>
      )}

      {view === 'month' && <MonthView cursor={cursor} events={allEvents} feestdagen={feestdagen} onEventClick={onEventClick} onCellClick={onCellClick} onEventDragStart={onEventDragStart} onCellDrop={onCellDrop} />}
      {view === 'week' && <WeekView cursor={cursor} events={allEvents} feestdagen={feestdagen} onEventClick={onEventClick} onCellClick={onCellClick} onEventDragStart={onEventDragStart} onCellDrop={onCellDrop} onEventResize={onEventResize} />}
      {view === 'day' && <DayView cursor={cursor} events={allEvents} feestdagen={feestdagen} onEventClick={onEventClick} onCellClick={onCellClick} onEventDragStart={onEventDragStart} onCellDrop={onCellDrop} onEventResize={onEventResize} />}

      <EventModal
        open={modal.open}
        event={modal.event}
        defaultStart={modal.defaultStart}
        onClose={() => setModal({ open: false, event: null, defaultStart: null })}
        onSave={load}
        onDelete={load}
      />
    </div>
  );
}

// ─── Klikbaar label: open jaar+maand dropdown om direct te springen ──────────
function DatePickerLabel({ cursor, setCursor, label }) {
  const [open, setOpen] = useState(false);
  const currentYear = cursor.getFullYear();
  const currentMonth = cursor.getMonth();
  const monthsNl = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];
  // Jaren rond huidige (5 terug, 10 vooruit)
  const thisYear = new Date().getFullYear();
  const yearRange = [];
  for (let y = thisYear - 5; y <= thisYear + 10; y++) yearRange.push(y);

  const setDate = (year, month) => {
    const newDate = new Date(year, month, 1);
    setCursor(newDate);
    setOpen(false);
  };

  return (
    <div className="relative ml-3">
      <button
        onClick={() => setOpen(o => !o)}
        className="text-sm font-bold text-gray-800 capitalize hover:bg-gray-100 px-2 py-1 rounded inline-flex items-center gap-1.5"
        data-testid="agenda-date-label"
        title="Klik om direct te navigeren"
      >
        {label}
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 bg-white border border-gray-200 rounded shadow-lg z-50 p-3 w-[320px]" data-testid="agenda-date-picker">
            <div className="text-[10px] uppercase font-bold text-gray-500 mb-1">Jaar</div>
            <div className="grid grid-cols-4 gap-1 mb-3 max-h-32 overflow-y-auto">
              {yearRange.map(y => (
                <button
                  key={y}
                  onClick={() => setDate(y, currentMonth)}
                  className={`text-xs py-1.5 rounded border ${y === currentYear ? 'bg-[#002FA7] text-white border-[#002FA7] font-bold' : y === thisYear ? 'bg-blue-50 border-blue-300 text-blue-900' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                  data-testid={`agenda-pick-year-${y}`}
                >{y}</button>
              ))}
            </div>
            <div className="text-[10px] uppercase font-bold text-gray-500 mb-1">Maand</div>
            <div className="grid grid-cols-3 gap-1">
              {monthsNl.map((m, idx) => (
                <button
                  key={m}
                  onClick={() => setDate(currentYear, idx)}
                  className={`text-xs py-1.5 rounded border ${idx === currentMonth ? 'bg-[#002FA7] text-white border-[#002FA7] font-bold' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                  data-testid={`agenda-pick-month-${idx}`}
                >{m}</button>
              ))}
            </div>
            <div className="flex gap-2 mt-3 pt-2 border-t">
              <button
                onClick={() => { setCursor(new Date()); setOpen(false); }}
                className="flex-1 text-xs py-1.5 bg-blue-50 text-blue-700 rounded font-semibold hover:bg-blue-100"
                data-testid="agenda-pick-today"
              >📌 Vandaag</button>
              <button
                onClick={() => setOpen(false)}
                className="flex-1 text-xs py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                data-testid="agenda-pick-close"
              >Sluiten</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

